import {TechseeBaseController} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-controller';
import {
    MeetingMode,
    ReasonForClosing,
    UserType,
    RECORDING_STATUS,
    ClosedByTypes
} from '@techsee/techsee-common/lib/constants/room.constants';
import {IRoom} from '../../../models/Room';
import {action, computed, observable, reaction, runInAction} from 'mobx';
import {ConnectionStatesEnum, SocketVersionConfig} from '../../../services/Session/SessionContracts';
import {IGeneralApiService} from '@techsee/techsee-client-infra/lib/services/GeneralApiService';
import {IRoomService} from '@techsee/techsee-client-infra/lib/services/RoomService';
import {HistoryService} from '@techsee/techsee-client-infra/lib/services/HistoryService';
import {DashboardState} from '../../dashboard/_contracts/DashboardState';
import {RouteName} from '../../../router/route-names';
import {InvitationEndedReason} from '../../invitation-ended/controller';
import get from 'lodash/get';
import {onSocketVersionHandler} from '../../../services/Session/SessionEventHandler';
import {NetworkInfo} from '../../../models/LiveSessionState';
import {EventLogConstant} from '../../../constants/events/event-log.constant';
import {getRootStore} from '../../../app.bootstrap';
import {IEventLogsService} from '../../../services/EventsLogService';
// @ts-ignore
import {LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';
import {PlatformType} from '@techsee/techsee-common/lib/constants/utils.constant';
import {TsTimedOperationService} from '../../../services/ts-timed-operation/ts-timed-operation.service';
import {ENDING_TIMEOUT} from './MultipartyDashboardSettings';
import includes from 'lodash/includes';
import {SessionClientId, SessionClientRole} from '@techsee/techsee-media-service/lib/MediaConstants';
import assign from 'lodash/assign';
import {getAppTracer} from '../../../../app.tracer';
import {ITsReactStateHelper} from '../../../services/ts-react-state-helper/ts-react-state-helper.service';
import {IVisibilityChange} from '../../../services/ts-visibility-change/ts-visibility-change.service';
import {LeftBarController} from './components/layouts/miniDashboardLayout/leftBar/LeftBarController';
import {
    IVideoController,
    IVideoParticipantContext,
    VideoController,
    VideoControllerEvents
} from './components/video/VideoController';
import {IMediaInputStateEvent, MediaInputStateService} from './services/MediaInputStateService';
import {
    AudioState,
    createMultipartyController,
    MultipartyScreenShareStateChangedEvent,
    MultipartySessionSettings,
    PinningId,
    MultipartyEvents,
    MultipartyStreamPropertyChangedEvent
} from '@techsee/techsee-nway-media-service-client';
import {LiveLeftBarController} from './components/layouts/liveDashboardLayout/leftBar/LiveLeftBarController';
import {DashboardTopBarController} from '../../dashboard/dashboard-top-bar/controller';
import {TempAccountSettings} from '../../../layouts/main-layout/main-top-bar/controller.base';
import {SimpleModalController} from '../../../components/simple-modal/controller';
import {VideoLayoutType} from '@techsee/techsee-common/lib/constants/media.constants';
import {SnapshotService} from './services/SnapshotService';
import {GalleryController} from './components/gallery/GalleryController';
import {uuidv4} from '@techsee/techsee-common/lib/core/uuid';
import {ParticipantData} from '../../participant/participant-state.module';
import {resolveLogoUrl} from '../../../utils/logo-url.utils';
import {VideoFilterType} from '@techsee/techsee-common/lib/constants/account.constants';
import {convertImageToBase64} from './utils';
import {IChatApi} from '../../../services/ts-chat-api/ChatApi';
import {ITsLocaleHelperService} from '../../../services/ts-locale-helper/ts-locale-helper.service';
import {CONNECTION_STATUS} from '../../../services/ts-locale-helper/ts-locale-helper.settings';
import {ISharingService} from '../../../services/ts-sharing-service/contracts';
import {IBrowserUtilsService} from '@techsee/techsee-client-infra/lib/services/BrowserUtilsService';
import {ShareScreenPopupController} from '../../../components/twoWay-share-screen/controller';
import {IJsApiService, MessageType} from '../../../services/JsApiService';
import {requireImage} from '../../../utils/utils';

const trace = getAppTracer('MultipartyDashboardController');

type Coordinates = {
    lon: number;
    lat: number;
    altitude: number;
    accuracy: number;
};

type ParticipantState = {
    connected: boolean;
    firstName: string;
    lastName: string;
    participantId: string;
    updatedAt: number;
    status?: 'pending' | 'approved' | 'rejected';
};

export class MultipartyDashboardController extends TechseeBaseController {
    private _connectionStatus: any;
    private _connected: any;
    private eventParams: any = {};

    public readonly miniDashboard: boolean;
    public readonly embeddedDashboard: boolean;

    private _playAudio: boolean;
    private _playVideo?: boolean;

    private sessionInitializationInProgress: boolean = false;
    private multipartyControllerInitializationInProgress: boolean = false;
    private meetingSyncEnabled: boolean = false;
    private clientMediaPermissionsPopupShown = false;
    private storedParticipant: ParticipantData | undefined;

    public readonly videoController: IVideoController;
    public readonly liveLeftBarController: LiveLeftBarController;
    public readonly liveTopBarController: DashboardTopBarController;
    public readonly leftBarController: LeftBarController;
    public readonly galleryController: GalleryController;

    private _generalApiService: IGeneralApiService;
    private _eventsLogService: IEventLogsService;
    private _timedOperation: TsTimedOperationService;
    private _visibilityChange: IVisibilityChange;
    private _roomService: IRoomService;
    private _mediaInputStateService?: MediaInputStateService;
    private _sessionEndModalController?: SimpleModalController;
    private _customerEndedTheMeetingModalController?: SimpleModalController;
    private _clientWebRtcNotSupportedModalController?: SimpleModalController;
    private snapshotService: SnapshotService;
    private currentParticipant?: ParticipantData;
    private _videoFilterType?: VideoFilterType;
    private _observerVideoFilterType?: VideoFilterType;
    private _browserUtilsService: IBrowserUtilsService;
    private _jsApiService: IJsApiService;

    @observable
    private _customerEndedTheMeeting: boolean = false;

    @observable
    private lastEvent: {text?: string; timestamp?: number} = {};

    @observable
    public nwayParticipants: ParticipantState[] = [];

    @observable
    public waitingForHostApproval: boolean = false;

    @computed
    get pendingParticipants(): ParticipantState[] {
        return this.nwayParticipants
            .filter((p) => (p.status || 'approved') === 'pending' && p.connected !== false)
            .sort((a, b) => a.updatedAt - b.updatedAt);
    }

    @observable
    private videoLayoutType: VideoLayoutType = VideoLayoutType.AgentPinnedLayout;

    @observable
    public shareScreenPopupController: ShareScreenPopupController;

    @observable private _isDesktopView: boolean = false;
    @observable private _isPortrait: boolean = false;
    @observable private _enabledDesktopSharingInNWay: boolean = false;
    @observable private _clientAlreadyConnected: boolean = false;

    constructor(
        private readonly _localeHelper: ITsLocaleHelperService,
        private readonly _tsChatApi: IChatApi,
        private readonly dashboardState: DashboardState,
        private readonly _logEvents: EventLogConstant,
        private readonly _tsStateHelper: ITsReactStateHelper,
        private readonly sharingService: ISharingService
    ) {
        super();
        this._localeHelper.clear();
        this._connectionStatus = CONNECTION_STATUS;
        this._connected = CONNECTION_STATUS.WAITING;
        this._eventsLogService = getRootStore().eventsLogService;
        this._generalApiService = getRootStore().generalApiService;
        this._timedOperation = getRootStore().timedOperationService;
        this._browserUtilsService = getRootStore().browserUtilsService;
        this._roomService = getRootStore().roomService;

        this.miniDashboard = getRootStore().browserUtilsService.getFromSessionStorage('miniDashboard');
        this.embeddedDashboard = getRootStore().browserUtilsService.getFromSessionStorage('embeddedDashboard');
        this.currentParticipant = getRootStore().browserUtilsService.getFromSessionStorage('nway-participant');
        this._visibilityChange = getRootStore().visibilityChangeService;
        this._jsApiService = getRootStore().jsApiService;

        this.shareScreenPopupController = new ShareScreenPopupController(this.translate);

        this.galleryController = new GalleryController();
        const authToken = getRootStore().authService.getAuthToken();
        const apiUrl = getRootStore().urlConfigService.get('API_URL');
        const historyService = new HistoryService(apiUrl, authToken);

        this.snapshotService = new SnapshotService(window, this.sharingService, historyService, this._tsChatApi);
        this.videoController = new VideoController(
            this.dashboardState.roomData,
            this._tsChatApi,
            this._roomService,
            this.eventLog,
            this.faceMeetMediaPermission,
            this as IVideoParticipantContext
        );

        (this.videoController as VideoController).on(
            VideoControllerEvents.removeParticipant,
            (participantId: string) => {
                this.removeParticipant(participantId);
            }
        );

        this.setLastEvent = this.setLastEvent.bind(this);

        this._playAudio =
            getRootStore().browserUtilsService.getFromSessionStorage('playAudio') ??
            (this.dashboardState.roomData.initiateWithAudio || this.dashboardState.audioRequestedFromUrl);
        this._playVideo =
            getRootStore().browserUtilsService.getFromSessionStorage('playVideo') ??
            this.dashboardState.roomData.initiateWithVideo;

        this._visibilityChange.visible(() => this._tsChatApi.visibilityChanged(true));
        this._visibilityChange.hidden(() => this._tsChatApi.visibilityChanged(false));

        const portrait = window?.matchMedia('(orientation: portrait)');

        if (portrait) {
            this.setIsPortrait(portrait?.matches);

            portrait.addEventListener('change', (e) => {
                this.setIsPortrait(e?.matches);
            });
        }

        const isDesktop = getRootStore().environmentService.isDesktop(false);

        this.setIsDesktopView(isDesktop);

        const isEnableDesktopSharing = this._isDesktopView || this.desktopSharingToMobile;

        this._enabledDesktopSharingInNWay =
            this.dashboardState.accountData.protectedSettings?.v2_1?.enabled &&
            this.dashboardState.accountData.protectedSettings?.desktopSharing.enabled &&
            this.dashboardState.accountData.protectedSettings?.faceMeet.videoLayout ===
                VideoLayoutType.BigMainVideoLayout &&
            isEnableDesktopSharing;

        /**
         * Check if previous session is on and disconnect from it
         * @TODO move to prepare/check method in tsChatApi service
         */
        if (this._tsChatApi.roomId) {
            this._tsChatApi.disconnect();
        }

        const disconnectParticipant = () => {
            this.disconnectFromMeeting();
            void this._tsStateHelper.navigateTo(RouteName.INVITATION_ENDED);
        };
        // Report room as started to avoid having the room be reported as connected before it is started
        this._roomService.start(this.dashboardState.roomData._id);
        this.leftBarController = new LeftBarController(
            this.dashboardState,
            () =>
                this.isObserver ? disconnectParticipant() : this.endMeeting(this._logEvents.closedByTypes.dashboard),
            this.shareScreenPopupController,
            this._enabledDesktopSharingInNWay,
            this.translate,
            this.setLastEvent,
            this.dashboardState.accountData.protectedSettings.desktopSharing.enableRemoteControl,
            this._isDesktopView
        );
        this.liveLeftBarController = new LiveLeftBarController(this.translate, this.shareScreenPopupController);

        const tempAccSettings: TempAccountSettings = {
            generalSettings: {
                emailImageSharing: get(this.dashboardState.accountData, 'protectedSettings.emailImageSharing'),
                localImageSaving: get(this.dashboardState.accountData, 'protectedSettings.localImageSaving'),
                logoUrl: this.dashboardState.accountData.settings.logoUrl,
                enableMobileGeolocation: this.dashboardState.accountData.protectedSettings.enableMobileGeolocation,
                nativeAppId: get(this.dashboardState.accountData, 'protectedSettings.liveSdk.nativeAppId')
            },
            inviteMethodSettings: {
                multiLanguage: this.dashboardState.accountData.settings.multiLanguage
            }
        };

        this.liveTopBarController = new DashboardTopBarController(
            tempAccSettings,
            this.dashboardState,
            this.translate,
            (_, closeBy) => {
                if (this._customerEndedTheMeeting) {
                    this.endMeeting(closeBy);
                } else {
                    this._sessionEndModalController?.show();
                }
            },
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            this.leaveOfflineRoom.bind(this)
        );

        const videoFilterType = get(this.dashboardState.roomData, 'videoFilterType') as VideoFilterType;
        const virtualBackgroundSettings =
            get(this.dashboardState.accountData, 'protectedSettings.faceMeet.virtualBackground') || {};

        const isBlurAndSupported =
            videoFilterType === VideoFilterType.BLUR && virtualBackgroundSettings.enabledBlurAgent;

        const isVirtualBackgroundAndSupported =
            videoFilterType === VideoFilterType.BACKGROUND && virtualBackgroundSettings.enabledImageReplacementAgent;

        const isVirtualBackgroundEnabledInFaceMeet = virtualBackgroundSettings.enabledInFaceMeet;

        this._videoFilterType =
            isVirtualBackgroundEnabledInFaceMeet && (isBlurAndSupported || isVirtualBackgroundAndSupported)
                ? videoFilterType
                : VideoFilterType.NONE;

        const useBlurForObserver =
            isVirtualBackgroundEnabledInFaceMeet && virtualBackgroundSettings.enabledBlurCustomer;

        this._observerVideoFilterType = useBlurForObserver ? VideoFilterType.BLUR : VideoFilterType.NONE;

        this._onMediaInputStateEvent = this._onMediaInputStateEvent.bind(this);

        this.eventLog(LOG_EVENTS.userAgent, {
            side: PlatformType.dashboard,
            userAgent: window.navigator.userAgent,
            clientVersion: CLIENT_VERSION //@ts-ignore
        });

        this.connectToMeeting();
        this._initSimpleModals();
        this.downloadInitialSnapshots();

        reaction(
            () => ({
                layoutType: this.videoLayoutType,
                canTakeSnapshot: this.videoController.canTakeSnapshot
            }),
            ({layoutType, canTakeSnapshot}) => {
                this.videoController.setVideoLayoutType(layoutType);

                if (layoutType === VideoLayoutType.BigMainVideoLayout) {
                    this.videoController.onSnapshotRequested =
                        !this.miniDashboard && canTakeSnapshot ? this.onSnapshotRequested : undefined;
                    this.leftBarController.onSnapshotRequested = this.miniDashboard
                        ? this.onSnapshotRequested
                        : undefined;
                    this.leftBarController.isSnapshotEnabled = canTakeSnapshot;
                }
            }
        );
    }

    leaveOfflineRoom() {
        this.videoController.multipartyController?.disconnect();

        this._tsChatApi.disconnect();

        void this._tsStateHelper.navigateTo(RouteName.INVITATION_ENDED);
    }

    @computed
    public get showGallery(): boolean {
        return this.videoLayoutType === VideoLayoutType.BigMainVideoLayout;
    }

    @computed
    public get isObserver(): boolean {
        return this.dashboardState.isObserve;
    }

    public get isParticipant() {
        return this.isObserver;
    }

    public get isAgent(): boolean {
        return !this.dashboardState.isObserve;
    }

    public get logoUrl(): string {
        if (this.currentParticipant?.isSf) return requireImage('sf-logo.png');

        return resolveLogoUrl(this.dashboardState.accountData?.settings?.logoUrl);
    }

    // Recording API (parity with legacy dashboard controller)
    public get isRecordingEnabled(): boolean {
        return (
            !this.isObserver &&
            get(this.dashboardState.accountData, 'protectedSettings.recording.onDemand.enabled') &&
            get(this._tsChatApi, 'client.mode') === MeetingMode.faceMeet &&
            get(this.dashboardState.accountData, 'protectedSettings.enableNWay')
        );
    }

    public get recordingStatus(): RECORDING_STATUS {
        return get(this._tsChatApi, 'dashboard.recordingStatus');
    }

    public get isRecording(): boolean {
        return this.recordingStatus === RECORDING_STATUS.started;
    }

    public get recordButtonReady(): boolean {
        return (
            [RECORDING_STATUS.ready, RECORDING_STATUS.started].includes(this.recordingStatus as any) &&
            get(this._tsChatApi, 'areBothSidesConnected') &&
            get(this._tsChatApi, 'client.faceMeetHandshakeSuccess') === true
        );
    }

    public recordingStatusMessage(): string {
        switch (this.recordingStatus) {
            case RECORDING_STATUS.ready:
            case RECORDING_STATUS.failed:
                return this.translate('REACT.FLOATING_TOOLBAR.RECORD.START');
            case RECORDING_STATUS.started:
                return this.translate('REACT.FLOATING_TOOLBAR.RECORD.STOP');
            case RECORDING_STATUS.stopping:
                return this.translate('REACT.FLOATING_TOOLBAR.RECORD.SAVING');
            case RECORDING_STATUS.starting:
                return this.translate('REACT.FLOATING_TOOLBAR.RECORD.STARTING');
            default:
                return this.translate('REACT.FLOATING_TOOLBAR.RECORD.START');
        }
    }

    public onRecordButton(): Promise<any> | void {
        if (this.recordingStatus === RECORDING_STATUS.started) {
            return this.stopRecording();
        } else if (this.recordingStatus === RECORDING_STATUS.ready) {
            return this.startRecording();
        }
    }

    private recordStatusChanged(status: RECORDING_STATUS): void {
        this._tsChatApi.setStatus('recordingStatus', status, false);
        this.emit('recordingStatusChanged', status);
    }

    private startRecording(): Promise<any> {
        this.recordStatusChanged(RECORDING_STATUS.starting);

        const RECORDING_FAILED_TIMEOUT = 15000;
        setTimeout(() => {
            if (this.recordingStatus === RECORDING_STATUS.starting) {
                this.recordStatusChanged(RECORDING_STATUS.failed);
            }
        }, RECORDING_FAILED_TIMEOUT);

        return this._roomService
            .startRecording(this.dashboardState.roomData._id, {
                accountId: this.dashboardState.accountData._id
            })
            .then(() => {
                this.recordStatusChanged(RECORDING_STATUS.started);
            })
            .catch(() => this.recordStatusChanged(RECORDING_STATUS.failed));
    }

    private stopRecording(): Promise<any> {
        this.recordStatusChanged(RECORDING_STATUS.stopping);

        const RECORDING_FAILED_TIMEOUT = 15000;
        setTimeout(() => {
            if (this.recordingStatus === RECORDING_STATUS.stopping) {
                this.recordStatusChanged(RECORDING_STATUS.failed);
            }
        }, RECORDING_FAILED_TIMEOUT);

        return this._roomService
            .stopRecording(this.dashboardState.roomData._id, {
                accountId: this.dashboardState.accountData._id
            })
            .then(() => {
                this.recordStatusChanged(RECORDING_STATUS.ready);
            })
            .catch(() => this.recordStatusChanged(RECORDING_STATUS.failed));
    }

    private downloadInitialSnapshots = async () => {
        const urls = await this.snapshotService.fetchSnapshotUrlsFromHistory(this.dashboardState.roomData._id);

        urls.forEach((image) => this.galleryController.addImage(uuidv4(), image));
    };

    private onSnapshotRequested = async () => {
        const id = uuidv4();
        const snapshot = this.videoController.takeSnapshot();

        if (!snapshot) {
            return;
        }

        this.galleryController.addImageLoadingPlaceholder(id);
        const uploadResult = await this.snapshotService.uploadSnapshot(snapshot);

        if (uploadResult) {
            this.galleryController.addImage(id, snapshot);
        } else {
            this.galleryController.addImageFailedPlaceholder(id);
        }
    };

    private eventLog = (logType: string, meta?: any): Promise<void> =>
        this._eventsLogService.info({
            logType,
            userId: this.dashboardState.currentUser._id,
            accountId: this.dashboardState.currentUser.accountId,
            roomId: this.dashboardState.roomData._id,
            meta
        });

    private initHandlers() {
        this._tsChatApi.on('sync', () => this.syncMeeting());

        // eslint-disable-next-line complexity
        this._tsChatApi.on('log', (log: any, sender: any, isNew: boolean) => {
            switch (log.name) {
                case 'MEDIA_PERMISSION_REJECTED':
                    this.eventLog(LOG_EVENTS.mediaPermissionRejected);
                    this._mediaInputStateService?.mediaPermissionRejected();
                    break;
                case 'CUSTOMER_ENDED_THE_MEETING':
                    runInAction(() => {
                        this._customerEndedTheMeeting = true;
                    });
                    this.terminateMediaInputStateEvents();
                    this.videoController.multipartyController?.disconnect();
                    this._customerEndedTheMeetingModalController?.show();

                    this._jsApiService.sessionEnded({
                        type: MessageType.sessionEnded,
                        sessionId: this.dashboardState.roomData._id,
                        techseeSessionId: get(this.dashboardState.roomData, 'guids', [])[0] ?? '',
                        customerNumber: this.dashboardState.roomData.customerNumber,
                        customerId: this.dashboardState.roomData._id
                    } as any);
                    if (this.isObserver) {
                        void this._tsStateHelper.navigateTo(RouteName.INVITATION_ENDED);
                    } else if (this.miniDashboard) {
                        void this._tsStateHelper.navigateTo(RouteName.SESSION_ENDED);
                    }

                    break;
                case 'CLIENT_LOCATION_DENIED':
                    this.eventLog(LOG_EVENTS.clientLocationDenied);
                    break;
                case 'CLIENT_LOCATION_ERROR':
                    this.eventLog(LOG_EVENTS.clientLocationError);
                    break;
                case 'CLIENT_LOCATION':
                    if (this._tsChatApi.client.location) {
                        this.eventLog(LOG_EVENTS.clientLocation, {coords: this._tsChatApi.client.location});
                        this._getAddress(this._tsChatApi.client.location).then(() =>
                            this._localeHelper.handleMessage(log, sender, isNew)
                        );
                    }
                    break;
                case 'TOS_REJECTED':
                    this._shouldRouteToSessionEndedState();
                    break;
                default:
                    break;
            }

            if (this._shouldHandleLogMessage(log.name)) {
                this._localeHelper.handleMessage(log, sender, isNew);
            }
        });

        this._tsChatApi.on('endMeetingAction', () => {
            this.endMeeting(this._logEvents.closedByTypes.simpleApi);
        });
        this._tsChatApi.on('imageSave', async (e) => {
            if (this.isObserver) {
                this.galleryController.addImage(uuidv4(), e.data.message);
            }
        });
        this._tsChatApi.on('socketVersion', (message: SocketVersionConfig) => onSocketVersionHandler(message));
        this._tsChatApi.on('clientMode', () => {
            const clientMode = get(this._tsChatApi, 'client.mode');
            clientMode && this._roomService.changeAgent(this.dashboardState.roomData._id, clientMode.toUpperCase());
        });

        this._tsChatApi.on('clientConnected', () => {
            const clientConnected = get(this._tsChatApi, 'client.connected');

            if (clientConnected) {
                this._roomService.connect(this.dashboardState.roomData._id);
                this._clientAlreadyConnected = true;

                this._localeHelper.mobileClientConnected();

                if (!get(this._tsChatApi, 'client.visible') && this._connected !== this._connectionStatus.IDLE) {
                    this._localeHelper.mobileClientHidden();
                    this._connected = this._connectionStatus.IDLE;
                }

                return;
            }

            this._localeHelper.mobileClientDisconnected();
            this._clientAlreadyConnected = false;
        });
        this._tsChatApi.on('clientLocation', (param: any, coords: any) => {
            this._getAddress(coords);
        });
        this._tsChatApi.on('roomRejected', () => {
            this._tsChatApi.sendLog(new Error('Room rejected'));
        });
        this._tsChatApi.on('clientVisible', (param: any, state: any) => {
            if (state) {
                this._localeHelper.mobileClientVisible();
            } else {
                this._localeHelper.mobileClientHidden();
            }

            this._connected = state ? this._connectionStatus.CONNECTED : this._connectionStatus.IDLE;
        });
        this._tsChatApi.on('nwayParticipants', (nwayParticipants: ParticipantState[]) => {
            const prevParticipants = this.nwayParticipants;

            runInAction(() => {
                this.nwayParticipants = nwayParticipants;
            });

            if (this._handleParticipantSelfWatch(nwayParticipants)) {
                return;
            }

            const lastChange = [...nwayParticipants].sort((a, b) => b.updatedAt - a.updatedAt)[0];

            if (!lastChange || lastChange.participantId === this.currentParticipant?.id) {
                return;
            }

            if (this._handleApprovalToast(lastChange, prevParticipants)) {
                return;
            }

            this._logParticipantConnectivity(lastChange);
        });
        this._tsChatApi.on('mobileNetworkType', (networkInfo: any) => {
            const mobileNetworkInfo = NetworkInfo.fromDataObject(networkInfo);

            runInAction(() => {
                if (this.dashboardState) {
                    this.dashboardState.clientNetworkInfo.downlinkMax = mobileNetworkInfo.downlinkMax;
                    this.dashboardState.clientNetworkInfo.connectionType = mobileNetworkInfo.connectionType;
                }
            });

            const type = mobileNetworkInfo.connectionType;

            switch (type.toLowerCase()) {
                case 'cellular':
                    this._localeHelper.mobileNetworkCell();
                    break;
                case 'unsupported':
                case 'wifi':
                    this._localeHelper.mobileNetworkWifi();
                // eslint-disable-next-line no-fallthrough
                default:
                    break;
            }
        });
        this._tsChatApi.on('roomUpdated', (customerId: any) => {
            this.dashboardState.roomData.customerId = customerId;
        });

        this._tsChatApi.on('dashboardFacemeetBigVideoPinnedUser', (pinningId: PinningId) => {
            this.videoController.multipartyController?.setPinnedUser(pinningId);
        });

        this._localeHelper.on('newEvent', (eventLog: any) => {
            this.handleLog(eventLog);
        });

        this._tsChatApi.on('disconnectObserver', () => {
            if (this.isObserver) {
                this._tsChatApi.disconnect();
                this._connected = CONNECTION_STATUS.DISCONNECTED;
                const navParams = this.waitingForHostApproval
                    ? {err: true, reason: InvitationEndedReason.sessionExpired}
                    : undefined;

                void this._tsStateHelper.navigateTo(RouteName.INVITATION_ENDED, navParams);
            }
        });

        this._tsChatApi.on('removeParticipantAction', (data: {participantId: string}) => {
            if (data?.participantId === this.currentParticipant?.id) {
                this.eventLog(LOG_EVENTS.ParticipantRemovedFromSession, {participantId: data.participantId});
                void this._tsStateHelper.navigateTo('invitationEnded', {reason: 'removedByAgent'});
                this.disconnectFromMeeting();
            }
        });

        this._localeHelper.on('clientConnectionStatus', (status: any) => (this._connected = status));
    }

    _shouldHandleLogMessage(logName: string) {
        switch (logName) {
            case 'CLIENT_LOCATION':
                return false;
            case 'CUSTOMER_IN_PHOTOGALLERY_PAGE':
                return !this.isObserver;
            case 'CLIENT_CONNECTED':
                return !this._clientAlreadyConnected;
            default:
                return true;
        }
    }

    _getAddress(coords: Coordinates) {
        return this._generalApiService
            .getLocationAddress({lon: coords.lon.toString(), lat: coords.lat.toString(), lang: 'en_US'})
            .then((res: any) => {
                const formattedAddress = res.address || res[0]?.formattedAddress || '';

                this.eventParams.address = formattedAddress;
                this._updateTopbarAddress(coords, formattedAddress);
                this._localeHelper.updateLogs();
                this.eventLog(LOG_EVENTS.clientLocationAddress, {formattedAddress});
            })
            .catch(() => {
                // if retrieval fails, just show the coordinates
                this.eventParams.address = `${coords.lon}, ${coords.lat}`;
                this._updateTopbarAddress(coords);
                this._localeHelper.updateLogs();
                this.eventLog(LOG_EVENTS.clientLocationAddressError);
            })
            .finally(() => {
                if (this.embeddedDashboard) {
                    const data: {x: number; y: number; address: string; z?: number; accuracy?: number} = {
                        x: coords.lon,
                        y: coords.lat,
                        address: this.eventParams.address || ''
                    };

                    if (coords.altitude) {
                        data.z = coords.altitude;
                    }

                    if (coords.accuracy) {
                        data.accuracy = coords.accuracy;
                    }

                    this._roomService.customerGeolocation(this.dashboardState.roomData._id, data);
                }
            });
    }

    handleLog(eventLog: any) {
        const text = this.translate(eventLog.text, {
            device: get(this.eventParams, 'device'),
            address: get(this.eventParams, 'address')
        });

        if (!text) {
            return;
        }

        runInAction(() => {
            this.lastEvent = {
                text,
                timestamp: new Date().getTime()
            };
        });
    }

    dispose() {
        this.terminateMediaInputStateEvents();

        if (this.videoController?.multipartyController) {
            this.videoController.multipartyController.disconnect();
        }
    }

    disconnectFromMeeting() {
        this._tsChatApi.disconnect(); //socket
        this.terminateMediaInputStateEvents();
        this.videoController.multipartyController?.disconnect(); //session
        this._connected = CONNECTION_STATUS.DISCONNECTED;
    }

    removeParticipant(participantId: string): void {
        this._tsChatApi.requestAction('removeParticipant', {participantId});
    }

    admitParticipant = (participantId: string): Promise<void> => this._reviewParticipant(participantId, 'admit');

    rejectParticipant = (participantId: string): Promise<void> => this._reviewParticipant(participantId, 'reject');

    private _reviewParticipant(participantId: string, decision: 'admit' | 'reject'): Promise<void> {
        return this._tsChatApi
            .requestActionWithAck('reviewParticipant', {participantId, decision})
            .then(() => undefined);
    }

    endMeeting(closedBy: string, reason?: ReasonForClosing) {
        return Promise.resolve()
            .then(() => {
                if (!this._timedOperation.lockAttempt(ENDING_TIMEOUT)) {
                    return Promise.resolve();
                }

                const meeting = this._tsChatApi.dashboard.meeting;

                this._tsChatApi.setStatus('meeting', false);

                this.disconnectFromMeeting();

                if (meeting) {
                    return this._roomService
                        .endRoom(this.dashboardState.roomData._id, {
                            closedBy: closedBy as ClosedByTypes,
                            reasonForClosing: reason
                        })
                        .catch((err: any) => console.error(err));
                }

                return Promise.resolve();
            })
            .finally(() => {
                this._timedOperation.release();
                this._shouldRouteToSessionEndedState();
            });
    }

    _shouldRouteToSessionEndedState() {
        if (this.embeddedDashboard) {
            void this._tsStateHelper.navigateTo(RouteName.SESSION_ENDED);
        } else {
            void this._tsStateHelper.navigateTo(RouteName.INVITE);
        }
    }

    connectToMeeting() {
        trace.info('Connecting to meeting');

        const initialSyncHandler = () => {
            trace.info('Initial sync');
            this.initHandlers();

            Promise.resolve()
                .then(() => this.syncLanguage())
                .then(() => (this.meetingSyncEnabled = true))
                .then(() => this.syncMeeting())
                .catch((error) => {
                    this.meetingSyncEnabled = true;
                    trace.warn('Error during start meeting flow', error && error.toString());
                });
        };

        this._tsChatApi.once('sync', initialSyncHandler);

        if (this.isObserver) {
            if (!this.currentParticipant) {
                throw new Error('Current participant is not defined');
            }

            this._tsChatApi.connect(
                this.dashboardState.roomData._id,
                UserType.supervisor,
                undefined,
                this.currentParticipant.id
            );
        } else {
            const roomCode = this.dashboardState.roomCode;

            this._tsChatApi.connect(this.dashboardState.roomData._id, UserType.dashboard, roomCode);
        }
    }

    syncMeeting() {
        if (!this.meetingSyncEnabled) {
            trace.info('Sync arrived from BE, but not ready to sync yet');

            return Promise.resolve();
        }

        // After agent refresh, list is only filled via STATUS_CHANGED; sync already has dashboard.nwayParticipants.
        const fromDashboard = get(this._tsChatApi, 'dashboard.nwayParticipants');
        if (Array.isArray(fromDashboard) && fromDashboard.length > 0 && this.nwayParticipants.length === 0) {
            runInAction(() => {
                this.nwayParticipants = [...fromDashboard];
            });
        }

        trace.info('Start syncMeeting flow');

        return Promise.resolve()
            .then(() => this.syncRoom())
            .then(() => this.syncMediaSession())
            .catch((error) => {
                trace.info('Sync meeting flow error', error);
            });
    }

    syncLanguage() {
        this._tsChatApi.setStatus('language', this._localeHelper.getLocale());

        return Promise.resolve();
    }

    private initMediaInputStateEvents() {
        try {
            if (this.miniDashboard && this._mediaInputStateService) {
                this._mediaInputStateService.on('mediaInputState', this._onMediaInputStateEvent);
            }
        } catch (error) {
            console.error('Error initializing media input state events:', error);
        }
    }

    private terminateMediaInputStateEvents() {
        try {
            if (this._mediaInputStateService) {
                this._mediaInputStateService.off('mediaInputState', this._onMediaInputStateEvent);
                this._mediaInputStateService.terminateHandlers();
                this._mediaInputStateService = undefined;
            }
        } catch (error) {
            console.error('Error terminating media input state events:', error);
        }
    }

    _onMediaInputStateEvent(data: IMediaInputStateEvent) {
        try {
            const text = data.text;
            const timestamp = new Date().getTime();

            runInAction(() => {
                this.lastEvent = {text: this.translate(text), timestamp};
            });
        } catch (error) {
            console.error('Error handling media input state event:', error);
        }
    }

    private _handleParticipantSelfWatch(nwayParticipants: ParticipantState[]): boolean {
        if (!this.isObserver || !this.currentParticipant || !this.waitingForHostApproval) {
            return false;
        }

        const ownEntry = nwayParticipants.find((p) => p.participantId === this.currentParticipant!.id);

        if (ownEntry?.status === 'approved') {
            runInAction(() => {
                this.waitingForHostApproval = false;
                this.sessionInitializationInProgress = false;
            });
            this.syncMediaSession();

            return true;
        }

        if (ownEntry?.status === 'rejected' || !ownEntry) {
            this._tsStateHelper.navigateTo(RouteName.INVITATION_ENDED, {
                err: true,
                reason: InvitationEndedReason.requestDenied
            });

            return true;
        }

        return false;
    }

    private _handleApprovalToast(lastChange: ParticipantState, prevParticipants: ParticipantState[]): boolean {
        if ((lastChange.status || 'approved') !== 'approved') {
            return false;
        }

        const prevEntry = prevParticipants.find((p) => p.participantId === lastChange.participantId);
        const wasJustApproved = prevEntry && (prevEntry.status || 'approved') === 'pending';

        if (wasJustApproved || (!lastChange.status && lastChange.connected)) {
            const logText = `${lastChange.firstName} ${lastChange.lastName} ${this.translate('LOGS.PARTICIPANT_CONNECTED')}`;

            this.handleLog({text: logText});

            return true;
        }

        return false;
    }

    private _logParticipantConnectivity(lastChange: ParticipantState): void {
        if (lastChange.status) {
            return;
        }

        const logText =
            `${lastChange.firstName} ${lastChange.lastName} ` +
            this.translate(lastChange.connected ? 'LOGS.PARTICIPANT_CONNECTED' : 'LOGS.PARTICIPANT_DISCONNECTED');

        this.handleLog({text: logText});
    }

    faceMeetMediaPermissionFailed = () => {
        this._tsChatApi.sendLog('MEDIA_PERMISSION_REJECTED');
        this._tsChatApi.setStatus('mediaPermissionsAccepted', false);
        this._tsChatApi.setStatus('mediaPermissionsRejected', true);
    };
    faceMeetMediaPermissionSuccess = () => {
        this.eventLog(LOG_EVENTS.mediaPermissionAllow);
        this._tsChatApi.setStatus('mediaPermissionsAccepted', true);
        this._tsChatApi.setStatus('mediaPermissionsRejected', false);
    };

    faceMeetMediaPermission = (data: any): void => {
        if (data?.mediaDeviceAccessDenied) {
            this.faceMeetMediaPermissionFailed();

            return;
        }

        this.faceMeetMediaPermissionSuccess();
    };

    isToggleEnabled_SessionToolbar() {
        return get(this.dashboardState.accountData, 'protectedSettings.enableSessionToolbar');
    }

    isToggleEnabled_IdleModeUsability() {
        return get(this.dashboardState.accountData, 'protectedSettings.enableIdleModeUsability');
    }

    // eslint-disable-next-line eslint-comments/disable-enable-pair
    /* eslint-disable complexity */
    syncMediaSession() {
        const clientMode = get(this._tsChatApi, 'client.mode');

        const clientWebRtcSupported = get(this._tsChatApi, 'client.webRtcSupported') !== false;

        if (!clientWebRtcSupported && !this.clientMediaPermissionsPopupShown) {
            this.clientMediaPermissionsPopupShown = true;
            this._clientWebRtcNotSupportedModalController?.show();
        }

        if (!clientMode) {
            //No need to connect to media session while mode is unknown
            return Promise.resolve();
        }

        const mediaSessionModes = [MeetingMode.faceMeet];
        const isMediaSessionMode = includes(mediaSessionModes, this._tsChatApi.client.mode);

        if (this.sessionInitializationInProgress) {
            return Promise.resolve();
        }

        if (isMediaSessionMode) {
            const mediaSessionParams: {
                credentials: {sessionId?: string; token?: string; tokenClient?: string; apiKey?: string};
            } = {
                credentials: {}
            };

            let paramsReadyPromise = Promise.resolve();

            this.sessionInitializationInProgress = true;

            const syncOTCredentials = async () => {
                let opentokToken = this.dashboardState.roomData.opentok.tokenAgent;
                let sessionClientId: SessionClientId = SessionClientRole.AGENT;

                if (this.isObserver) {
                    sessionClientId = SessionClientRole.OBSERVER;
                    const enableNWayApproval = get(this._tsChatApi, 'accountSettings.enableNWayApproval', false);

                    if (enableNWayApproval) {
                        const ownEntry = this.nwayParticipants.find(
                            (p) => p.participantId === this.currentParticipant!.id
                        );
                        const participantStatus = ownEntry ? ownEntry.status || 'approved' : 'pending';

                        if (participantStatus === 'pending') {
                            runInAction(() => {
                                this.waitingForHostApproval = true;
                                this.sessionInitializationInProgress = false;
                            });

                            return;
                        }

                        // Approved — fetch OT token via HTTP
                        const guid = get(this.dashboardState.roomData, 'guids', [])[0];

                        if (!guid) {
                            trace.warn('Cannot fetch participant OT token: guid is unavailable');
                            this.sessionInitializationInProgress = false;

                            return;
                        }

                        const tokenData = await this._generalApiService.getNwayParticipantToken(
                            guid,
                            this.currentParticipant!.id
                        );

                        opentokToken = tokenData.opentokToken;
                        runInAction(() => {
                            this.dashboardState.roomData.opentok.sessionId = tokenData.sessionId;
                            this.dashboardState.roomData.opentok.apiKey = tokenData.apiKey;
                        });
                    } else {
                        opentokToken = this.dashboardState.roomData.opentok.tokenNWayParticipants?.find(
                            (t: {participantId: string; token: string}) =>
                                t.participantId === this.currentParticipant!.id
                        )?.token;
                    }
                }

                this.dashboardState.roomData.opentok.token = opentokToken;

                mediaSessionParams.credentials = assign({}, this.dashboardState.roomData.opentok);

                const selectedCamera = get(
                    this.dashboardState.accountData,
                    'protectedSettings.faceMeet.agentDefaultCamera'
                );
                const agentStartSessionWithVideo = get(
                    this.dashboardState.accountData,
                    'protectedSettings.faceMeet.agentStartSessionWithVideo'
                );
                const cameraEnabled = this._playVideo !== undefined ? this._playVideo : agentStartSessionWithVideo;

                const backgroundReplacementImg = await this.getBackgroundReplacementImg();

                const mediaServiceSettings: MultipartySessionSettings = {
                    cameraEnabled,
                    selectedCamera,
                    mediaServiceType: this.dashboardState.roomData.mediaServiceType,
                    audioState: !this._playAudio ? AudioState.MUTED : AudioState.ENABLED,
                    videoFilter: this.isObserver ? this._observerVideoFilterType : this._videoFilterType,
                    backgroundReplacementImg,
                    ipWhitelist: get(this.dashboardState.accountData, 'protectedSettings.useOnlyOpenTokAllowedIPS')
                };
                const isPeerMediaPermissionRejected =
                    !this._tsChatApi.client.mediaPermissionsAccepted && this._tsChatApi.client.mediaPermissionsRejected;

                const {sessionId, token, apiKey} = mediaSessionParams.credentials;

                if (
                    !this.multipartyControllerInitializationInProgress &&
                    !this.videoController.multipartyController &&
                    sessionId &&
                    token &&
                    apiKey
                ) {
                    this.multipartyControllerInitializationInProgress = true;

                    createMultipartyController({sessionId, token, apiKey}, mediaServiceSettings, sessionClientId)
                        .then((controller) => {
                            try {
                                const pinnedUser = this._tsChatApi.dashboard.facemeetBigVideoPinnedUser;
                                this.shareScreenPopupController.setShareScreenCallback(
                                    controller.shareScreen,
                                    controller.stopShareScreen
                                );

                                controller.setParticipantDetails(this.storedParticipant!);
                                if (pinnedUser && pinnedUser !== 'none') {
                                    controller.setPinnedUser(pinnedUser);
                                } else {
                                    controller.setPinnedUser(SessionClientRole.USER);
                                }

                                controller.on(
                                    MultipartyEvents.screenShareStateChanged,
                                    (e: MultipartyScreenShareStateChangedEvent) => {
                                        let currentId: string = SessionClientRole.AGENT;
                                        if (this.isObserver) {
                                            currentId = this.currentParticipant!.id;
                                        }

                                        this.liveLeftBarController.changeScreenSharingButtonState(e.isScreenSharing);

                                        if (e.clientId !== currentId) {
                                            this.liveLeftBarController.disableScreenSharingButton(e.isScreenSharing);
                                            this.leftBarController.setScreenShareDisabled(e.isScreenSharing);
                                        }

                                        this.eventLog(LOG_EVENTS.clickOnDesktopShare, e);
                                    }
                                );

                                controller.on(
                                    MultipartyEvents.streamPropertyChanged,
                                    (e: MultipartyStreamPropertyChangedEvent) => {
                                        if (e.isOwnConnection) {
                                            const browserUtilsService = getRootStore().browserUtilsService;

                                            browserUtilsService.saveToSessionStorage('playVideo', e.hasVideo);
                                            browserUtilsService.saveToSessionStorage('playAudio', e.hasAudio);
                                        }
                                    }
                                );

                                this.videoController.setMultipartyController(controller);
                                try {
                                    if (!this._mediaInputStateService && this.videoController.multipartyController) {
                                        this._mediaInputStateService = new MediaInputStateService(
                                            this.videoController.multipartyController,
                                            !this._playAudio,
                                            this.dashboardState.roomData._id
                                        );

                                        this.initMediaInputStateEvents();
                                    }
                                } catch (error) {
                                    console.error('Error creating MediaInputStateService:', error);
                                }
                            } catch (error) {
                                console.error('Error setting up multiparty controller:', error);
                            }
                        })
                        .catch((error) => {
                            console.error('Error creating multiparty controller:', error);
                        })
                        .finally(() => {
                            this.multipartyControllerInitializationInProgress = false;
                        });
                }
                this.videoController.setIsPeerMediaPermissionsRejected(isPeerMediaPermissionRejected);
            };

            if (!this.dashboardState.roomData.opentok.init || this.isObserver) {
                const userType = this.isObserver ? UserType.supervisor : UserType.dashboard;
                let participantId;

                if (userType === UserType.supervisor) {
                    participantId = this.currentParticipant?.id;
                }

                // Get participant names from session storage
                let firstName, lastName;
                try {
                    this.storedParticipant = this._browserUtilsService.getFromSessionStorage('nway-participant');
                    if (this.storedParticipant) {
                        firstName = this.storedParticipant.firstName;
                        lastName = this.storedParticipant.lastName;
                    }
                } catch (error) {
                    console.warn('Failed to parse participant data from session storage:', error);
                }

                paramsReadyPromise = this._roomService
                    .initOpentok(this.dashboardState.roomData._id, userType, participantId, `${firstName} ${lastName}`)
                    .then((roomData) => {
                        this.dashboardState.roomData = roomData as unknown as IRoom;
                        syncOTCredentials();
                    });
            } else {
                syncOTCredentials();
            }

            return paramsReadyPromise
                .then(() => {
                    if (
                        !mediaSessionParams.credentials ||
                        Object.getOwnPropertyNames(mediaSessionParams.credentials).length < 2
                    ) {
                        return Promise.resolve();
                    }

                    this._tsChatApi.setStatus('opentok', {
                        session: {
                            sessionId: mediaSessionParams.credentials.sessionId,
                            token: mediaSessionParams.credentials.tokenClient,
                            apiKey: mediaSessionParams.credentials.apiKey
                        }
                    });

                    return Promise.resolve();
                })
                .catch((error) => {
                    trace.warn('error during connection to media session', error);
                })
                .finally(() => (this.sessionInitializationInProgress = false));
        } else if (!isMediaSessionMode) {
            // if (isSessionActive) {
            //     disconnectFromSession()
            // }
        }

        return Promise.resolve();
    }

    syncRoom() {
        const {faceMeetVideoLayout} = this._tsChatApi?.accountSettings;

        // Initial connectivity, in case user already connected during invite
        if (this._connected === CONNECTION_STATUS.WAITING && this._tsChatApi.client.connected) {
            this._connected = CONNECTION_STATUS.CONNECTED;
        }

        const clientDisconnected = !this._tsChatApi.client.connected && this._tsChatApi.client.connected !== undefined;
        const isOffline = clientDisconnected || this._connected === this._connectionStatus.IDLE;

        runInAction(() => {
            if (this.dashboardState) {
                this.dashboardState.connectionState = isOffline
                    ? ConnectionStatesEnum.OFFLINE
                    : ConnectionStatesEnum.HIGH;
            }
            if (faceMeetVideoLayout) {
                // @ts-ignore
                this.videoLayoutType = faceMeetVideoLayout;
            }
        });

        if (this._tsChatApi.dashboard.roomStarted || clientDisconnected) {
            return Promise.resolve(this.dashboardState.roomData);
        }

        return this._roomService.start(this.dashboardState.roomData._id).then((roomData) => {
            this.dashboardState.roomData = roomData as unknown as IRoom;

            this._tsChatApi.setStatus('roomStarted', true);

            return this.dashboardState.roomData;
        });
    }

    private _initSimpleModals() {
        this._sessionEndModalController = new SimpleModalController(
            '',
            this.translate('REACT.MULTIPARTY_DASHBOARD.END_SESSION_MODAL.TITLE'),
            this.translate('REACT.MULTIPARTY_DASHBOARD.END_SESSION_MODAL.CONTENT'),
            this.translate('REACT.COMMON.BUTTONS.CANCEL'),
            this.translate('REACT.MULTIPARTY_DASHBOARD.END_SESSION_MODAL.CONFIRM'),
            () => {
                this.endMeeting(EventLogConstant.closedByTypes.dashboard);
            }
        );

        this._customerEndedTheMeetingModalController = new SimpleModalController(
            '',
            this.translate('REACT.MULTIPARTY_DASHBOARD.CUSTOMER_ENDED_THE_SESSION_MODAL.TITLE'),
            this.translate('REACT.MULTIPARTY_DASHBOARD.CUSTOMER_ENDED_THE_SESSION_MODAL.CONTENT'),
            undefined,
            this.translate('REACT.COMMON.BUTTONS.OK')
        );

        this._clientWebRtcNotSupportedModalController = new SimpleModalController(
            '',
            this.translate('REACT.MULTIPARTY_DASHBOARD.WEBRTC_MODAL_TITLE'),
            this.translate('REACT.MULTIPARTY_DASHBOARD.WEBRTC_MODAL_MESSAGE'),
            undefined,
            this.translate('REACT.MULTIPARTY_DASHBOARD.CANCEL_BUTTON'),
            () => this._clientWebRtcNotSupportedModalController?.hide(),
            undefined
        );
    }

    private _updateTopbarAddress(coords: Coordinates, formattedAddress = '') {
        const {lat, lon, altitude, accuracy} = coords;
        const googleMapsQuery = formattedAddress ? encodeURIComponent(formattedAddress) : `${lat},${lon}`;
        const googleMapsLink = `https://www.google.com/maps/search/?api=1&query=${googleMapsQuery}`;

        runInAction(() => {
            this.dashboardState.clientLocationInfo = {
                lat,
                lon,
                altitude,
                accuracy,
                formattedAddress,
                googleMapsLink,
                isDenied: false,
                isFailed: false
            };
        });
    }

    private getBackgroundReplacementImg = async (): Promise<string | undefined> => {
        let imgUrl: string | undefined;

        if (
            get(this.dashboardState.accountData, 'protectedSettings.faceMeet.virtualBackground.enabledInFaceMeet') &&
            this.dashboardState.roomData.videoFilterType === VideoFilterType.BACKGROUND &&
            get(
                this.dashboardState.accountData,
                'protectedSettings.faceMeet.virtualBackground.enabledImageReplacementAgent'
            )
        ) {
            try {
                const url = await getRootStore().resourcesService.getDefault(this.dashboardState.currentUser.accountId);
                imgUrl = await convertImageToBase64(url as unknown as string);
                this.eventLog(LOG_EVENTS.gettingImageBackgroundResourceSuccess, {imgUrl});
            } catch (err) {
                trace.warn('Error getting default background image', JSON.stringify(err));
                this.eventLog(LOG_EVENTS.gettingImageBackgroundResourceFailed, {error: err});
            }
        }

        return imgUrl;
    };
    get notificationMessage() {
        return this.lastEvent.text;
    }

    get isDesktopView(): boolean {
        return this._isDesktopView;
    }

    get desktopSharingToMobile(): boolean {
        return get(this.dashboardState.accountData, 'protectedSettings.v2_1.desktopSharingToMobile');
    }

    @action
    private setLastEvent(text: string, timestamp?: number): void {
        runInAction(() => {
            this.lastEvent = {text, timestamp};
        });
    }

    @action
    private setIsDesktopView(isDesktopView: boolean): void {
        this._isDesktopView = isDesktopView;
    }

    @computed
    get isPortrait() {
        return this._isPortrait;
    }

    @action
    private setIsPortrait(isPortrait: boolean): void {
        this._isPortrait = isPortrait;
    }

    get sessionEndModalController() {
        return this._sessionEndModalController;
    }

    @computed
    get customerEndedTheMeeting() {
        return this._customerEndedTheMeeting;
    }

    get customerEndedTheMeetingModalController() {
        return this._customerEndedTheMeetingModalController;
    }

    get clientWebRtcNotSupportedModalController() {
        return this._clientWebRtcNotSupportedModalController;
    }

    get allowRemoveParticipant(): boolean {
        return !!get(this.dashboardState.accountData, 'protectedSettings.allowRemoveParticipant');
    }

    @computed
    get showLoader() {
        return this.videoController.multipartyController === undefined;
    }
}
